import { CommonModule } from '@angular/common';
import { afterNextRender, ChangeDetectorRef, Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroBanknotes, heroCalendarDays, heroCircleStack, heroComputerDesktop, heroCreditCard,
  heroDocumentText, heroHashtag, heroIdentification, heroReceiptPercent, heroServerStack,
  heroShieldCheck, heroTag, heroUser, heroWallet
} from '@ng-icons/heroicons/outline';
import { finalize } from 'rxjs';
import {
  GpTransactionRow,
  PageResponse,
  TransactionApi,
  TransactionFilterValue
} from '../../core/transaction-api';
import { brandImage, gpDateTime, moneyFromCents, statusClass, text } from '../../shared/formatters';
import { PageNavigation } from '../../shared/page-navigation/page-navigation';

type DetailTabId = 'operation' | 'payment' | 'card' | 'response' | 'system';
type DetailField = { key: string; label: string; icon: string };
type DetailTab = { id: DetailTabId; label: string; icon: string; fields: DetailField[] };

@Component({
  selector: 'app-transactions-gp',
  imports: [CommonModule, FormsModule, PageNavigation, NgIcon],
  templateUrl: './transactions-gp.html',
  providers: [provideIcons({
    heroBanknotes, heroCalendarDays, heroCircleStack, heroComputerDesktop, heroCreditCard,
    heroDocumentText, heroHashtag, heroIdentification, heroReceiptPercent, heroServerStack,
    heroShieldCheck, heroTag, heroUser, heroWallet
  })]
})
export class TransactionsGp {
  readonly statuses = ['APPROVED', 'DECLINED', 'REVERSAL', 'PROCESSING', 'ERROR', 'UNAVAILABLE', 'FRAUD_SUSPECTED', 'UNKNOWN'];
  readonly transactionTypes = ['PURCHASE', 'VOID', 'REFUND', 'REVERSAL', 'WITHDRAWAL', 'AUTHORIZATION', 'BALANCE_INQUIRY', 'RECONCILIATION', 'ADMIN', 'NETWORK_MANAGEMENT'];
  readonly brands = ['VISA', 'MASTERCARD', 'MAESTRO', 'CABAL', 'NARANJA', 'AMEX'];
  readonly cardTypes = ['CREDIT', 'DEBIT', 'PREPAID'];
  readonly entryModes = ['MANUAL', 'MAGNETIC_STRIPE', 'CONTACT', 'CONTACTLESS', 'ECOMMERCE', 'QR'];
  readonly pageSizes = [10, 25, 50, 100];

  rows: GpTransactionRow[] = [];
  filters: Record<string, TransactionFilterValue> = {};
  page = 0;
  size = 10;
  total = 0;
  loading = false;
  detailLoading = false;
  filtersOpen = false;
  error = '';
  detail?: Record<string, unknown>;
  activeDetailTab: DetailTabId = 'operation';

  readonly detailTabs: DetailTab[] = [
    { id: 'operation', label: 'Operacion', icon: '#', fields: [
      { key: 'id', label: 'ID', icon: '#' }, { key: 'transactionId', label: 'Transaccion', icon: 'TX' },
      { key: 'externalReference', label: 'Referencia externa', icon: 'RF' }, { key: 'transactionDateTime', label: 'Fecha', icon: 'FE' },
      { key: 'dateTimeTransaction', label: 'Fecha GP', icon: 'FE' }, { key: 'transactionType', label: 'Operacion', icon: 'OP' },
      { key: 'transactionStatus', label: 'Estado', icon: 'ST' }, { key: 'terminalId', label: 'Terminal', icon: 'TE' },
      { key: 'originalTransactionId', label: 'Transaccion original', icon: 'TX' }, { key: 'submerchantId', label: 'Subcomercio', icon: 'SM' }
    ]},
    { id: 'payment', label: 'Pago', icon: '$', fields: [
      { key: 'amountTransaction', label: 'Monto', icon: '$' }, { key: 'currency', label: 'Moneda', icon: 'MO' },
      { key: 'installment', label: 'Cuotas', icon: 'CU' }, { key: 'installmentPlan', label: 'Plan de cuotas', icon: 'PL' },
      { key: 'walletName', label: 'Wallet', icon: 'WA' }
    ]},
    { id: 'card', label: 'Tarjeta', icon: 'CC', fields: [
      { key: 'cardBrand', label: 'Marca', icon: 'BR' }, { key: 'cardType', label: 'Tipo', icon: 'CC' },
      { key: 'posEntryMode', label: 'Entry mode', icon: 'EM' }, { key: 'stan', label: 'STAN', icon: 'STN' },
      { key: 'bin', label: 'BIN', icon: 'CC' },
      { key: 'l4d', label: 'Ultimos 4', icon: 'CC' }, { key: 'card', label: 'PAN', icon: 'CC' },
      { key: 'cvm', label: 'CVM', icon: 'CV' },
      { key: 'cardHolderName', label: 'Titular', icon: 'TH' },
      { key: 'cardHolderIdentificationDocument', label: 'Documento', icon: 'ID' }
    ]},
    { id: 'response', label: 'Respuesta', icon: 'RS', fields: [
      { key: 'transactionResponseDataId', label: 'Response data ID', icon: 'ID' }, { key: 'responseCode', label: 'Codigo', icon: 'RS' },
      { key: 'rrn', label: 'RRN', icon: 'RR' }, { key: 'authorizationCode', label: 'Autorizacion', icon: 'AU' },
      { key: 'additionalResponseData', label: 'Datos adicionales', icon: 'DA' }, { key: 'responseDateTime', label: 'Fecha de respuesta', icon: 'FE' },
      { key: 'responseAmount', label: 'Monto respuesta', icon: '$' }, { key: 'responseTerminalId', label: 'Terminal respuesta', icon: 'TE' }
    ]},
    { id: 'system', label: 'Sistema', icon: 'S', fields: [
      { key: 'createdAt', label: 'Creada', icon: 'CR' }, { key: 'updatedAt', label: 'Actualizada', icon: 'UP' }
    ]}
  ];

  readonly iconMap: Record<string, string> = {
    '#': 'heroHashtag', TX: 'heroIdentification', RF: 'heroDocumentText', FE: 'heroCalendarDays', OP: 'heroTag',
    ST: 'heroShieldCheck', TE: 'heroComputerDesktop', SM: 'heroUser', '$': 'heroBanknotes', MO: 'heroBanknotes',
    CU: 'heroReceiptPercent', PL: 'heroDocumentText', WA: 'heroWallet', CC: 'heroCreditCard', BR: 'heroTag',
    EM: 'heroCreditCard', STN: 'heroHashtag', ID: 'heroIdentification', CV: 'heroShieldCheck', FP: 'heroShieldCheck',
    TH: 'heroUser', RS: 'heroCircleStack', RR: 'heroDocumentText', AU: 'heroShieldCheck', DA: 'heroDocumentText',
    SE: 'heroShieldCheck', RP: 'heroCircleStack', S: 'heroServerStack', CR: 'heroCalendarDays', UP: 'heroCalendarDays'
  };

  readonly text = text;
  readonly dateTime = gpDateTime;
  readonly moneyFromCents = moneyFromCents;
  readonly statusClass = statusClass;
  readonly brandImage = brandImage;

  constructor(private readonly api: TransactionApi, private readonly cdr: ChangeDetectorRef) {
    afterNextRender(() => this.load());
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.size));
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.api.getGpTransactions({ page: this.page, size: this.size, search: '', filters: this.cleanFilters() })
      .pipe(finalize(() => { this.loading = false; this.cdr.markForCheck(); }))
      .subscribe({
        next: (response: PageResponse<GpTransactionRow>) => {
          this.rows = response.data ?? [];
          this.total = response.recordsFiltered ?? response.recordsTotal ?? 0;
          this.cdr.markForCheck();
        },
        error: error => {
          console.error('Error loading GP transactions', error);
          this.error = 'No se pudieron cargar las transacciones GP.';
          this.cdr.markForCheck();
        }
      });
  }

  applyFilters(): void { this.page = 0; this.load(); }
  clearFilters(): void { this.filters = {}; this.page = 0; this.load(); }
  previous(): void { if (this.page > 0 && !this.loading) { this.page--; this.load(); } }
  next(): void { if (this.page + 1 < this.totalPages && !this.loading) { this.page++; this.load(); } }

  openDetail(id: number): void {
    this.detail = undefined;
    this.detailLoading = true;
    this.activeDetailTab = 'operation';
    this.api.getGpDetail(id)
      .pipe(finalize(() => { this.detailLoading = false; this.cdr.markForCheck(); }))
      .subscribe({
        next: detail => { this.detail = detail; this.cdr.markForCheck(); },
        error: () => { this.detail = { error: 'No se pudo cargar el detalle.' }; this.cdr.markForCheck(); }
      });
  }

  closeDetail(): void { this.detail = undefined; }

  detailValue(key: string): unknown { return this.detail?.[key]; }

  activeDetailItems(): DetailField[] {
    const tab = this.detailTabs.find(item => item.id === this.activeDetailTab);
    return tab?.fields.filter(field => this.hasValue(this.detailValue(field.key))) ?? [];
  }

  detailIcon(icon: string): string { return this.iconMap[icon] ?? 'heroTag'; }

  formatDetail(key: string, value: unknown = this.detailValue(key)): string {
    if (key === 'amountTransaction' || key === 'responseAmount') return moneyFromCents(value);
    if (key.toLowerCase().includes('date') || key.endsWith('At')) return gpDateTime(value);
    return text(value);
  }

  private cleanFilters(): Record<string, TransactionFilterValue> {
    return Object.fromEntries(Object.entries(this.filters).filter(([, value]) => this.hasValue(value)));
  }

  private hasValue(value: unknown): boolean {
    return value !== undefined && value !== null && value !== '' && (!Array.isArray(value) || value.length > 0);
  }
}
